import { useState } from "react";
import { NavLink } from "react-router-dom";
import { useUIStore } from "@admin/stores/uiStore";
import { useLogoutMutation } from "@shared/hooks/useAuthMutations";
import { Icons } from "@admin/components/icons/Icons";
import { Logo } from "@shared/components/Logo";
import { Modal } from "@admin/components/ui/Modal";

const navItems = [
  { href: "/admin/dashboard", label: "Dashboard", icon: Icons.Dashboard },
  { href: "/admin/productos", label: "Productos", icon: Icons.Package },
  { href: "/admin/categorias", label: "Categorías", icon: Icons.Tag },
  { href: "/admin/catalogos", label: "Catálogos", icon: Icons.Book },
  { href: "/admin/carrusels", label: "Carrusels", icon: Icons.Image },
  { href: "/admin/historial", label: "Historial", icon: Icons.History },
];

export function AdminSidebar() {
  const { isSidebarOpen, toggleSidebar } = useUIStore();
  const { logout } = useLogoutMutation();
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleConfirmLogout = () => {
    logout();
    setShowLogoutModal(false);
  };

  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  return (
    <>
      <aside
        className={`relative bg-[#282828] text-white flex flex-col h-full transition-all duration-300 ease-in-out ${
          isSidebarOpen ? "w-64" : "w-20"
        }`}
      >
        {/* Header del sidebar */}
        <div className="p-4 flex items-center justify-center border-b border-gray-700">
          <div
            className={`transition-opacity duration-200 ${
              !isSidebarOpen && "opacity-0"
            }`}
          >
            <Logo width={120} height={40} className="brightness-0 invert" />
          </div>
        </div>

        {/* Navegación */}
        <nav className="flex-grow p-4 space-y-2">
          {navItems.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              end
              className={({ isActive }) =>
                `flex items-center p-3 rounded-lg hover:bg-white/5 transition-colors ${
                  isActive ? "bg-white/10" : ""
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon className="h-6 w-6 flex-shrink-0" />
                  <span
                    className={`ml-4 transition-opacity duration-200 ${
                      !isSidebarOpen && "opacity-0"
                    } ${isActive ? "font-bold" : "font-normal"}`}
                  >
                    {item.label}
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Botón de Logout */}
        <div className="p-4 border-t border-gray-700">
          <button
            onClick={handleLogoutClick}
            className="w-full flex items-center p-3 rounded-lg hover:bg-white/5 transition-colors"
          >
            <Icons.LogOut className="h-6 w-6 flex-shrink-0" />
            <span
              className={`ml-4 transition-opacity duration-200 ${
                !isSidebarOpen && "opacity-0"
              }`}
            >
              Cerrar sesión
            </span>
          </button>
        </div>

        {/* Botón para colapsar/expandir el sidebar */}
        <button
          onClick={toggleSidebar}
          className="absolute -right-3 top-10 bg-[#282828] text-white p-1.5 rounded-full shadow-md hover:bg-[#3a3a3a] transition-all"
          aria-label={isSidebarOpen ? "Colapsar sidebar" : "Expandir sidebar"}
        >
          <div
            className={`transition-transform duration-300 ${
              isSidebarOpen ? "rotate-0" : "rotate-180"
            }`}
          >
            <Icons.ChevronLeft size={16} />
          </div>
        </button>
      </aside>

      {/* Modal de confirmación de logout */}
      <Modal
        isOpen={showLogoutModal}
        onClose={handleCancelLogout}
        title="Confirmar cierre de sesión"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            ¿Estás seguro de que deseas cerrar sesión? Se perderán todos los
            datos no guardados.
          </p>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={handleCancelLogout}
              className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmLogout}
              className="px-4 py-2 text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
            >
              Cerrar sesión
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
